<template>
  <div class="notifications-page">
    <div class="page-header">
      <div>
        <h1 class="page-title">Notifications</h1>
        <p class="page-subtitle">Stay updated with important alerts and updates</p>
      </div>
    </div>

    <div class="tabs" role="tablist" aria-label="Notification filters">
      <div class="tab active" role="tab" aria-selected="true">All Notifications</div>

      <div class="tab" role="tab" aria-selected="false">Archived</div>
    </div>

    <div class="content-grid">
      <div class="notif-panel">
        <template v-for="(group, dateLabel) in groupedNotifications" :key="dateLabel">
          <div class="group-label">{{ dateLabel }}</div>

          <div v-for="notif in group" :key="notif.id" :class="['notif-item', { unread: notif.unread }]">
            <div :class="['notif-icon', 'icon-' + notif.type]" aria-hidden="true">{{ notif.icon }}</div>
            <div class="notif-body">
              <div class="notif-top">
                <span class="notif-title">{{ notif.title }}</span>
                <span class="notif-time">{{ notif.time }}</span>
              </div>
              <div class="notif-badge-row">
                <span :class="['type-badge', 'badge-' + notif.type]">
                  {{ getBadgeLabel(notif.type) }}
                </span>
              </div>
              <p class="notif-text">{{ notif.text }}</p>
              <div class="notif-meta">
                <div class="notif-actions">
                  <button type="button" class="action-btn" :title="notif.unread ? 'Mark as read' : 'Mark as unread'"
                    @click="notif.unread = !notif.unread">
                    {{ notif.unread ? '✓' : '◎' }}
                  </button>
                  <button type="button" class="action-btn" title="Archive">🗂</button>
                  <button type="button" class="action-btn danger" title="Delete"
                    @click="deleteNotification(notif.id)">✕</button>
                </div>
                <span :class="['read-dot', notif.unread ? 'dot-unread' : 'dot-read']" aria-hidden="true"></span>
              </div>
            </div>
          </div>
        </template>

        <div class="load-more">
          Load More
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      <div>
        <div class="summary-panel">
          <div class="summary-title">Notification Summary</div>

          <div class="summary-item">
            <div class="summary-icon icon-update" aria-hidden="true">🔔</div>
            <div class="summary-info">
              <div class="summary-label">Updates &amp; Alerts</div>
              <div class="summary-count">{{ summaryCounts.update }}</div>
            </div>
            <span class="summary-arrow" aria-hidden="true">›</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-action" @click="markAllAsRead">
            Mark all as read
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { app } from '../../config/firebase';
import { getMessaging, isSupported, onMessage } from 'firebase/messaging';

export default {
  name: "Notifications",
  data() {
    return {
      // Start with an empty array. 
      // You should fetch your historical notifications from your backend API here
      notifications: []
    };
  },
  computed: {
    groupedNotifications() {
      return this.notifications.reduce((groups, notif) => {
        const date = notif.date || 'Today';
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push(notif);
        return groups;
      }, {});
    },
    summaryCounts() {
      return {
        broadcast: this.notifications.filter(n => n.type === 'broadcast').length,
        user: this.notifications.filter(n => n.type === 'user').length,
        update: this.notifications.filter(n => n.type === 'update').length
      };
    }
  },
  mounted() {
    this.setupFirebaseMessaging();
  },
  methods: {
    getBadgeLabel(type) {
      if (type === 'broadcast') return 'Broadcast';
      if (type === 'user') return 'User Specific';
      return 'Updates & Alerts';
    },
    markAllAsRead() {
      this.notifications.forEach(n => n.unread = false);
    },
    deleteNotification(id) {
      this.notifications = this.notifications.filter(n => n.id !== id);
    },
    async setupFirebaseMessaging() {
      if (!app) return;

      try {
        const supported = await isSupported();
        if (!supported) return;

        const messaging = getMessaging(app);

        // Listen for foreground messages
        onMessage(messaging, (payload) => {
          // Show a toast notification if the toast plugin is available
          if (this.$toast) {
            this.$toast.info(payload.notification?.title || 'New Notification');
          }

          // Add the new notification to the reactive list
          this.notifications.unshift({
            id: Date.now(),
            type: payload.data?.type || 'update',
            icon: payload.data?.icon || '🔔',
            title: payload.notification?.title || 'New Notification',
            text: payload.notification?.body || '',
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            date: 'Today',
            unread: true
          });
        });
      } catch (error) {
        console.error('[Notifications] Firebase messaging setup failed:', error.message);
      }
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap");

.notifications-page {
  --teal: #00968f;
  --teal-dark: #007a74;
  --teal-light: #e0f5f4;
  --slate-900: #1e2d3a;
  --slate-700: #2e4057;
  --slate-500: #5a7185;
  --slate-300: #c4d4df;
  --slate-100: #f2f6f8;
  --white: #ffffff;
  --amber: #f59e0b;
  --amber-light: #fef3c7;
  --red: #ef4444;
  --red-light: #fee2e2;
  --blue: #3b82f6;
  --blue-light: #eff6ff;
  --shadow-xs: 0 1px 3px rgba(0, 0, 0, 0.07);
  --radius-md: 12px;

  font-family: "Sora", sans-serif;
  color: var(--slate-900);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--slate-900);
  margin: 0;
}

.page-subtitle {
  font-size: 13px;
  color: var(--slate-500);
  margin: 2px 0 0;
}

.settings-btn {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 1.5px solid var(--slate-300);
  display: grid;
  place-items: center;
  background: var(--white);
  cursor: pointer;
  color: var(--slate-500);
  transition: all 0.18s;
}

.settings-btn:hover {
  border-color: var(--teal);
  color: var(--teal);
}

.tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid var(--slate-300);
  margin-bottom: 20px;
}

.tab {
  padding: 10px 20px;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--slate-500);
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  white-space: nowrap;
}

.tab.active {
  color: var(--teal);
  border-bottom-color: var(--teal);
  font-weight: 600;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 20px;
  align-items: start;
}

.notif-panel {
  background: var(--white);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-xs);
  overflow: hidden;
}

.group-label {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--slate-500);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 14px 20px 8px;
}

.notif-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--slate-100);
  position: relative;
}

.notif-item:last-of-type {
  border-bottom: none;
}

.notif-item:hover {
  background: #fafcfc;
}

.notif-item.unread {
  background: rgba(0, 150, 143, 0.03);
}

.notif-item.unread::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--teal);
  border-radius: 0 2px 2px 0;
}

.notif-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-size: 18px;
}

.icon-broadcast {
  background: var(--teal-light);
  color: var(--teal);
}

.icon-user {
  background: var(--blue-light);
  color: var(--blue);
}

.icon-update {
  background: var(--amber-light);
  color: var(--amber);
}

.notif-body {
  flex: 1;
  min-width: 0;
}

.notif-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.notif-title {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--slate-900);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-time {
  font-size: 11.5px;
  color: var(--slate-500);
  font-family: "DM Mono", monospace;
  white-space: nowrap;
  flex-shrink: 0;
}

.notif-badge-row {
  margin-bottom: 6px;
}

.type-badge {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.badge-broadcast {
  background: var(--teal-light);
  color: var(--teal);
}

.badge-user {
  background: var(--blue-light);
  color: var(--blue);
}

.badge-update {
  background: var(--amber-light);
  color: var(--amber);
}

.notif-text {
  font-size: 12.5px;
  color: var(--slate-500);
  line-height: 1.55;
  margin: 0;
}

.notif-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.read-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-unread {
  background: var(--teal);
}

.dot-read {
  background: var(--slate-300);
}

.notif-actions {
  display: none;
  gap: 4px;
  align-items: center;
}

.notif-item:hover .notif-actions {
  display: flex;
}

.action-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid var(--slate-300);
  background: var(--white);
  cursor: pointer;
  display: grid;
  place-items: center;
  color: var(--slate-500);
  font-size: 12px;
  transition: all 0.15s;
}

.action-btn:hover {
  border-color: var(--teal);
  color: var(--teal);
  background: var(--teal-light);
}

.action-btn.danger:hover {
  border-color: var(--red);
  color: var(--red);
  background: var(--red-light);
}

.load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid var(--slate-100);
  font-size: 13px;
  font-weight: 500;
  color: var(--slate-500);
  cursor: pointer;
}

.summary-panel {
  background: var(--white);
  border-radius: var(--radius-md);
  padding: 20px;
  box-shadow: var(--shadow-xs);
}

.summary-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--slate-900);
  margin-bottom: 16px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--slate-100);
  cursor: pointer;
}

.summary-item:last-of-type {
  border-bottom: none;
}

.summary-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-size: 16px;
}

.summary-info {
  flex: 1;
}

.summary-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--slate-900);
}

.summary-count {
  font-size: 18px;
  font-weight: 700;
  color: var(--teal);
  font-family: "DM Mono", monospace;
}

.summary-arrow {
  color: var(--slate-300);
  font-size: 12px;
}

.summary-divider {
  height: 1px;
  background: var(--slate-100);
  margin: 12px 0;
}

.summary-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--slate-700);
  cursor: pointer;
}

.summary-action svg {
  color: var(--slate-300);
}

@media (max-width: 900px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .notifications-page {
    padding: 14px;
  }

  .tabs {
    overflow-x: auto;
  }

  .tab {
    padding: 10px 14px;
    font-size: 12px;
  }
}
</style>
